/**
 * Grouped tab navigation for demo workbench panels.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { useKeyboardListNav } from "../hooks/index.js";

export interface DemoPanelGroupNavTab { readonly id: string; readonly label: React.ReactNode; }

export interface DemoPanelGroupNavGroup {
  readonly id: string; readonly label: React.ReactNode;
  readonly color?: string;
  readonly tabs: readonly DemoPanelGroupNavTab[];
}

export interface DemoPanelGroupNavProps {
  readonly groups: readonly DemoPanelGroupNavGroup[];
  readonly activeId: string; readonly onSelect: (id: string) => void;
  readonly tabpanelId: string; readonly ariaLabel?: string; readonly className?: string;
}

export function DemoPanelGroupNav({ groups, activeId, onSelect, tabpanelId, ariaLabel = "Panels", className }: DemoPanelGroupNavProps): React.JSX.Element {
  const itemRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  const focusOnArrowRef = useRef(false);
  const ids = useMemo(() => groups.flatMap((group) => group.tabs.map((tab) => tab.id)), [groups]);

  const registerRef = useCallback((id: string, node: HTMLButtonElement | null) => {
    if (node === null) itemRefs.current.delete(id);
    else itemRefs.current.set(id, node);
  }, []);

  const onSelectFromKey = useCallback(
    (id: string) => {
      focusOnArrowRef.current = true;
      onSelect(id);
    },
    [onSelect],
  );

  useEffect(() => {
    if (!focusOnArrowRef.current) return;
    focusOnArrowRef.current = false;
    itemRefs.current.get(activeId)?.focus();
  }, [activeId]);

  const { onKeyDown } = useKeyboardListNav<string>({ items: ids, activeId, onSelect: onSelectFromKey, orientation: "horizontal" });

  return (
    <nav className={className} aria-label={ariaLabel}>
      <div className="space-y-1" role="tablist" aria-label={ariaLabel} onKeyDown={onKeyDown}>
        {groups.map((group) => {
          const color = group.color ?? "#38bdf8";
          const hasActive = group.tabs.some((tab) => tab.id === activeId);
          return (
            <div key={group.id} className="grid gap-1 py-1 md:grid-cols-[4.75rem_1fr] md:items-start">
              <span
                className="pt-1 text-[10px] font-semibold uppercase tracking-normal md:text-right"
                style={{ color: hasActive ? color : "rgba(148,163,184,0.72)" }}
              >
                {group.label}
              </span>
              <div className="flex flex-wrap gap-1">
                {group.tabs.map((tab) => {
                  const active = tab.id === activeId;
                  return (
                    <button
                      key={tab.id}
                      ref={(node) => { registerRef(tab.id, node); }}
                      id={`tab-${tab.id}`}
                      role="tab"
                      type="button"
                      aria-selected={active ? "true" : "false"}
                      aria-controls={tabpanelId}
                      tabIndex={active ? 0 : -1}
                      onClick={() => { onSelect(tab.id); }}
                      className="rounded-md border px-2.5 py-1 text-[11px] transition"
                      style={{
                        background: active
                          ? `color-mix(in srgb, ${color} 16%, transparent)`
                          : "rgba(15,23,42,0.46)",
                        borderColor: active
                          ? `color-mix(in srgb, ${color} 46%, transparent)`
                          : "rgba(71,85,105,0.38)",
                        color: active ? color : "rgba(203,213,225,0.82)",
                        fontWeight: active ? 700 : 500,
                      }}
                    >
                      {tab.label}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </nav>
  );
}
